/**
 * Display helpers shared by the Adjust workspace (solution summary, point
 * table, detection review, trial review). Pure functions only; formatting of
 * numbers lives in `@/logic/statistics`.
 */

import {
  MAX_COMBINATIONS,
  MIN_REMAINING_PAIRS,
  getSolutionMode,
  type CombinatorialPlan,
} from '@/logic/adjustments';
import { isInFit } from '@/logic/weights';
import type {
  PointPair,
  PointResidual,
  SolutionMode,
  TransformationSolution,
} from '@/types';

/** Largest residual among pairs in the fit (Unity and Fixed). */
export const largestActiveResidual = (
  solution: TransformationSolution | null | undefined
): PointResidual | undefined =>
  solution?.residuals.reduce<PointResidual | undefined>(
    (best, residual) =>
      residual.excluded || residual.total === null
        ? best
        : !best || residual.total > (best.total ?? -Infinity)
          ? residual
          : best,
    undefined
  );

export const SOLUTION_MODES: Record<
  SolutionMode,
  { label: string; short: string; help: string }
> = {
  standard: {
    label: 'Weighted least squares',
    short: 'Least squares',
    help: 'All included pairs are solved together in a weighted least-squares Helmert fit. Three or more Fixed pairs act as high-weight control.',
  },
  minimallyConstrained: {
    label: 'Minimally constrained (1 Fixed)',
    short: '1 Fixed',
    help: 'One Fixed pair holds the translation exactly. Scale and rotation are solved from the remaining included pairs.',
  },
  constrained: {
    label: 'Constrained (2 Fixed)',
    short: '2 Fixed',
    help: 'Two Fixed pairs determine scale, rotation, and translation. Other included pairs only check that transform.',
  },
};

export const solutionModeOf = (solution: TransformationSolution) =>
  SOLUTION_MODES[getSolutionMode(solution)];

/** Project units as a label, or null when they are unspecified. */
export const unitsLabel = (units: string | null | undefined): string | null =>
  !units || units === 'unspecified' ? null : units;

/** Thousands-separated integer, e.g. 250000 → "250,000". */
export const formatCount = (value: number): string =>
  Math.round(value).toLocaleString('en-US');

/** "1 pair" / "3 pairs". */
export const pluralize = (count: number, noun: string, plural = `${noun}s`) =>
  `${formatCount(count)} ${count === 1 ? noun : plural}`;

/** Display label for a pair id, falling back to the id itself. */
export const pairLabelOf = (
  pairs: readonly PointPair[],
  id: number | null | undefined
): string => {
  if (id === null || id === undefined) return '—';
  const pair = pairs.find((candidate) => candidate.id === id);
  return pair?.label || pair?.sys1Point || String(id);
};

/** A pair is complete when all four coordinates are present. */
export const isCompletePair = (pair: PointPair): boolean =>
  [pair.n1, pair.e1, pair.n2, pair.e2].every((value) => value !== '');

/** Complete pairs in the fit (Unity or Fixed) that survive a rejection set. */
export const remainingActivePairs = (
  pairs: readonly PointPair[],
  rejectedIds: readonly number[]
): number =>
  pairs.filter(
    (pair) =>
      isInFit(pair.weight) &&
      !rejectedIds.includes(pair.id) &&
      isCompletePair(pair)
  ).length;

/** The single next step the summary offers as its main action. */
export type NextAction = 'solve' | 'resolve' | 'check' | 'export';

export const getNextAction = ({
  hasSolution,
  solutionStale,
  canCheck,
  detectionCurrent,
}: {
  hasSolution: boolean;
  solutionStale: boolean;
  /** Enough pairs in the fit for detection (three or more). */
  canCheck: boolean;
  /** Both detection methods have current results. */
  detectionCurrent: boolean;
}): NextAction => {
  if (!hasSolution) return 'solve';
  if (solutionStale) return 'resolve';
  if (canCheck && !detectionCurrent) return 'check';
  return 'export';
};

export const ALPHA_OPTIONS = [0.1, 0.05, 0.01, 0.001] as const;
export const DEFAULT_ALPHA = 0.001;

/** Option text for a rejection level: its cost, or why it is not offered. */
export const describeLevel = (plan: CombinatorialPlan): string => {
  const name = pluralize(plan.level, 'pair');
  if (plan.candidateCount < plan.level + MIN_REMAINING_PAIRS)
    return `${name} — needs ${plan.level + MIN_REMAINING_PAIRS} candidates`;
  if (plan.combinations > MAX_COMBINATIONS)
    return `${name} — ${formatCount(plan.combinations)} trials, over the limit`;
  return `${name} · ${pluralize(plan.combinations, 'trial')}`;
};
